const post = {
  slug: 'specific-script-better-visuals',
  cluster: 'scripting',
  eyebrow: 'Scripting',
  title: 'A vaguer script gives you worse pictures',
  description:
    'On a faceless channel, generic writing has a second cost most people never connect to the script: it determines what you are able to put on screen.',
  keywords: [
    'specific script', 'video visuals', 'b-roll', 'stock footage problem',
    'faceless video visuals', 'script specificity', 'video imagery',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/specific-script-better-visuals.webp',
    alt: 'Two search results side by side: a named subject returning real photographs, a generic phrase returning interchangeable stock.',
  },
  lede:
    'Everyone knows specific writing is better writing. On a faceless channel there is a second, more mechanical reason to care: **your script decides what footage exists to use.**',
  sections: [
    {
      heading: 'The mechanism',
      body: [
        {
          type: 'para',
          text: 'However you source visuals — a stock library, an image search, an archive, your own footage — you can only find what the script actually names.',
        },
        {
          type: 'example',
          badLabel: 'What the script says',
          goodLabel: 'What you can find',
          bad: '"An entrepreneur in the post-war years saw an opportunity in freight."',
          good: '"Malcolm McLean, a trucking operator from North Carolina, watched dockworkers unload his cargo by hand in nineteen fifty-six."',
          note: 'The first sentence can only be illustrated with generic stock. The second one has photographs of a real person, real docks, and a real year.',
        },
        {
          type: 'para',
          text: 'This is not a preference. It is a hard constraint on what the video can look like, decided at the writing stage and unfixable afterwards.',
        },
      ],
    },
    {
      heading: 'Why generic stock reads as cheap',
      body: [
        {
          type: 'para',
          text: 'Not because stock footage is bad — plenty of it is technically excellent. It reads as cheap because of the *relationship* between the picture and the words.',
        },
        {
          type: 'para',
          text: 'When a script says something specific and the image shows a generic version of it, the viewer registers the gap. A sentence about a named company over footage of an anonymous office is the video admitting it has no picture of the company. Nobody articulates this; everybody feels it.',
        },
        {
          type: 'para',
          text: 'The same clips also appear across hundreds of channels, because the pool of clips matching "person looking at laptop thoughtfully" is finite and heavily used.',
        },
      ],
    },
    {
      heading: 'Two kinds of specificity, two kinds of picture',
      body: [
        {
          type: 'para',
          text: 'Worth separating, because they solve different problems.',
        },
        {
          type: 'list',
          items: [
            '**Named things** — people, companies, places, products, events, dates. These give you *real photographs*. This is the who and the what.',
            '**Mechanisms** — how something works, why one thing causes another, what the numbers are. These give you *diagrams and charts*. This is the how and the why.',
          ],
        },
        {
          type: 'para',
          text: 'A script strong on names but with no mechanism produces a video of photographs with nothing to explain. A script strong on mechanism with no names produces diagrams floating free of anything real. Most good videos alternate.',
        },
      ],
    },
    {
      heading: 'What to write to get better pictures',
      body: [
        {
          type: 'list',
          items: [
            '**Name the entity** rather than the category. "Kodak", not "a photography company".',
            '**Give the date** rather than the era. Archives are indexed by date.',
            '**Name the place.** A city has photographs; "a factory town" does not.',
            '**State the figure.** A number is a chart; "a significant increase" is nothing.',
            '**Describe the physical thing** where there is one. What it looked like, how big it was, what it was made of.',
          ],
        },
        {
          type: 'para',
          text: 'Notice these are the same instructions you would get for better *writing*. That is the point — the two goals are the same goal, which is why it is worth doing even when the visuals are not your bottleneck.',
        },
      ],
    },
    {
      heading: 'The limit',
      body: [
        {
          type: 'para',
          text: 'This never licenses inventing a name to make the search easier. A specific claim that is false is considerably worse than a vague one that is true — it is checkable, and being caught inventing detail costs more credibility than a plain video ever costs in retention.',
        },
        {
          type: 'para',
          text: 'Where you genuinely do not have the specific, say the general thing honestly and accept the generic picture. See [handling uncertain facts](/blogs/handling-uncertain-facts-in-scripts).',
        },
        {
          type: 'quote',
          text: 'Specific and true is the target. Specific and false is the worst outcome available to you — worse than vague.',
        },
      ],
    },
    {
      heading: 'A check before you record',
      body: [
        {
          type: 'para',
          text: 'Read the script and, for each paragraph, write down what would be on screen. Any paragraph where the honest answer is "generic footage of something vaguely related" is a paragraph to rewrite more specifically — or to cut, because it is probably not saying much either.',
        },
        {
          type: 'para',
          text: 'Doing this before recording is much cheaper than discovering it at the edit, when the narration is already fixed.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'What if the topic genuinely has no images?',
      a: 'Then it wants diagrams, charts and typography rather than footage, and the script should be written to support those — which means being precise about mechanism and numbers. A topic with neither images nor mechanism is a hard one for this format.',
    },
    {
      q: 'Is AI-generated imagery a solution?',
      a: 'For abstract or conceptual illustration it can be. For anything about a real named person, place or event it is the wrong tool: a generated image of a real subject is a fabrication presented as documentation, which is a credibility problem rather than a stylistic choice.',
    },
  ],
  related: ['youtube-script-writing', 'handling-uncertain-facts-in-scripts', 'faceless-youtube-video-formats'],
};

export default post;
